import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  EnvelopeSimple, 
  Phone, 
  MapPin, 
  LinkedinLogo, 
  TwitterLogo,
  InstagramLogo,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageCookies = () => {
    // Clear consent and trigger the banner to show again
    localStorage.removeItem('atlas_cookie_consent');
    localStorage.removeItem('atlas_cookie_consent_date');
    // Dispatch custom event to notify the CookieConsent component
    window.dispatchEvent(new CustomEvent('manageCookiePreferences'));
    toast.info('Cookie preferences reset. Please select your preferences.');
  };

  const footerLinks = {
    solutions: [
      { label: 'Smart Agriculture Systems', href: '/solutions#smart-agriculture' },
      { label: 'Software Development', href: '/solutions#software' },
      { label: 'Health App Solutions', href: '/solutions#health' },
      { label: 'Satellite Services', href: '/solutions#satellite' },
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'News', href: '/news' },
      { label: 'Contact', href: '/contact' },
      { label: 'Admin', href: '/admin' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
      { label: 'Cookie Policy', href: '/cookie-policy' },
    ],
  };

  return (
    <footer 
      data-testid="main-footer"
      className={`relative overflow-hidden ${
        theme === 'dark' 
          ? 'bg-[#0A0A10] border-t border-[#464B8B]/20' 
          : 'bg-[#12131F] text-white'
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#464B8B]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#00C8EB]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_41b48c6f-4956-450b-8fcd-b0cffc364967/artifacts/fmzqkhge_Full-Color-Transparent.png"
                alt="ATLAS SOFTECH"
                className="h-12"
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              Empowering society, governments and enterprises with secure intelligent technology solutions.
            </p>
            <div className="space-y-3">
              <a href="mailto:contact@atlassoftech.com" className="flex items-center gap-3 text-gray-400 hover:text-[#00C8EB] transition-colors">
                <EnvelopeSimple size={20} weight="duotone" />
                contact@atlassoftech.com
              </a>
              <a href="tel:+255799227202" className="flex items-center gap-3 text-gray-400 hover:text-[#00C8EB] transition-colors">
                <Phone size={20} weight="duotone" />
                +255 799 227 202
              </a>
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin size={20} weight="duotone" />
                Dar Es Salaam, Tanzania
              </div>
            </div>
          </div>

          {/* Solutions Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">
              Solutions
            </h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#00C8EB] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#00C8EB] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase text-sm tracking-wider">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#00C8EB] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={handleManageCookies}
                  data-testid="manage-cookies-btn"
                  className="text-gray-400 hover:text-[#00C8EB] transition-colors text-left"
                >
                  Manage Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} ATLAS SOFTECH. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/112777005/admin/dashboard/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-linkedin"
              className="text-gray-400 hover:text-[#00C8EB] transition-colors"
            >
              <LinkedinLogo size={24} weight="duotone" />
            </a>
            <a
              href="https://x.com/Atlas_Softech"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-twitter"
              className="text-gray-400 hover:text-[#00C8EB] transition-colors"
            >
              <TwitterLogo size={24} weight="duotone" />
            </a>
            <a
              href="https://www.instagram.com/atlas_softech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              className="text-gray-400 hover:text-[#00C8EB] transition-colors"
            >
              <InstagramLogo size={24} weight="duotone" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
